// src/components/audit/AuditFilters.tsx
// Filter controls for the Audit Log page

import React from 'react';
import { Search, Calendar, Filter, X } from 'lucide-react';
import { AuditEventType, AUDIT_EVENT_TYPES } from '../../types/audit';

interface AuditFiltersProps {
  startDate: string;
  endDate: string;
  eventType: AuditEventType | '';
  searchQuery: string;
  onStartDateChange: (date: string) => void;
  onEndDateChange: (date: string) => void;
  onEventTypeChange: (eventType: AuditEventType | '') => void;
  onSearchChange: (query: string) => void;
  onClearFilters: () => void;
  hasActiveFilters: boolean;
}

const AuditFilters: React.FC<AuditFiltersProps> = ({
  startDate,
  endDate,
  eventType,
  searchQuery,
  onStartDateChange,
  onEndDateChange,
  onEventTypeChange,
  onSearchChange,
  onClearFilters,
  hasActiveFilters,
}) => {
  // Quick date range presets
  const setToday = () => {
    const today = new Date().toISOString().split('T')[0];
    onStartDateChange(today);
    onEndDateChange(today);
  };

  const setLast7Days = () => {
    const today = new Date();
    const weekAgo = new Date(today);
    weekAgo.setDate(weekAgo.getDate() - 7);
    onStartDateChange(weekAgo.toISOString().split('T')[0]);
    onEndDateChange(today.toISOString().split('T')[0]);
  };

  const setLast30Days = () => {
    const today = new Date();
    const monthAgo = new Date(today);
    monthAgo.setDate(monthAgo.getDate() - 30);
    onStartDateChange(monthAgo.toISOString().split('T')[0]);
    onEndDateChange(today.toISOString().split('T')[0]);
  };

  const setThisMonth = () => {
    const today = new Date();
    const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    onStartDateChange(firstOfMonth.toISOString().split('T')[0]);
    onEndDateChange(today.toISOString().split('T')[0]);
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 space-y-4">
      {/* Search Bar */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
        <input
          type="text"
          placeholder="Search by item name, serial number, or user..."
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
      </div>

      {/* Filter Row */}
      <div className="flex flex-wrap gap-4 items-end">
        {/* Date Range */}
        <div className="flex-1 min-w-[200px]">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            <Calendar className="inline h-4 w-4 mr-1" />
            Date Range
          </label>
          <div className="flex gap-2 items-center">
            <input
              type="date"
              value={startDate}
              onChange={(e) => onStartDateChange(e.target.value)}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
            />
            <span className="text-gray-500">to</span>
            <input
              type="date"
              value={endDate}
              onChange={(e) => onEndDateChange(e.target.value)}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
            />
          </div>
        </div>

        {/* Event Type Filter */}
        <div className="min-w-[200px]">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            <Filter className="inline h-4 w-4 mr-1" />
            Event Type
          </label>
          <select
            value={eventType}
            onChange={(e) => onEventTypeChange(e.target.value as AuditEventType | '')}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
          >
            <option value="">All Events</option>
            {AUDIT_EVENT_TYPES.map((type) => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>
        </div>

        {/* Clear Filters Button */}
        {hasActiveFilters && (
          <button
            onClick={onClearFilters}
            className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg flex items-center gap-1"
          >
            <X className="h-4 w-4" />
            Clear Filters
          </button>
        )}
      </div>

      {/* Quick Date Presets */}
      <div className="flex gap-2 flex-wrap">
        <span className="text-sm text-gray-500 mr-2">Quick:</span>
        <button
          onClick={setToday}
          className="px-3 py-1 text-xs border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-blue-500"
        >
          Today
        </button>
        <button
          onClick={setLast7Days}
          className="px-3 py-1 text-xs border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-blue-500"
        >
          Last 7 Days
        </button>
        <button
          onClick={setLast30Days}
          className="px-3 py-1 text-xs border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-blue-500"
        >
          Last 30 Days
        </button>
        <button
          onClick={setThisMonth}
          className="px-3 py-1 text-xs border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-blue-500"
        >
          This Month
        </button>
      </div>
    </div>
  );
};

export default AuditFilters;
